import type { ISectionedFilters } from "types/filters";
import type { TPaginationSortFilterOptions, TPaginationSortOptions } from "types/pagination";
import type { Require } from "types/utilTypes";
import type { TTicketDuration } from "utils/durationsOptions";

import { type List } from "immutable";
import { IntegrationResourceModel } from "models/IntegrationResourceModel";
import { apiReq, toQuerystring } from "utils/api/apiReq";
import { apiReqAbortOld } from "utils/api/apiReqAbortOld";
import { addSearchParam, fromPaginationSortFilterOptions, paginationSortFilterToBody } from "utils/api/urlSearchParams";
import { devLog } from "utils/devtools/devLogging";
import { type IPaginationResponse, withPagination } from "utils/pagination";
import { isStringArray } from "utils/strings";

export type TFullIntegrationResourceModel = Require<IntegrationResourceModel, "rolesCount">;

export type TIntegrationResourceBody = {
  allowedDurations?: List<TTicketDuration> | null;
  approvalAlgorithmId?: null | string;
  description?: null | string;
  id?: string;
  integrationId?: string;
  maintainers?: IIntegrationResourceMaintainer[];
  multirole?: boolean;
  name?: string;
  ownerUserId?: null | string;
  requestable?: boolean;
  tags?: null | string[];
  type?: string;
};

export type TIntegrationResourceCreateBody = TIntegrationResourceBody & {
  roles?: { name: string; virtualizedRoleId: string }[];
};

interface IIntegrationResourceMaintainer {
  id: string;
  type: "directoryGroup" | "user";
}

type TIntegrationResourceQueryString = {
  full?: boolean;
  withDeleted?: boolean;
  withUnmanaged?: boolean;
};

export async function addResourcePermission(id: string, roleId: string, integrationId?: string): Promise<TFullIntegrationResourceModel> {
  const integrationIdParam = integrationId ? `&integrationId=${integrationId}` : "";
  const url = `/v1/integrationResources/${id}/addResourcePermission?roleId=${roleId}${integrationIdParam}`;
  const { data } = await apiReq("POST", url);

  return IntegrationResourceModel.fromServerData(data) as TFullIntegrationResourceModel;
}

export async function createIntegrationResource(
  integrationResource: TIntegrationResourceCreateBody,
): Promise<IntegrationResourceModel> {
  const { data } = await apiReq("POST", "/v1/integrationResources", integrationResource);

  return IntegrationResourceModel.fromServerData(data);
}

export async function deleteIntegrationResource(id: string, integrationId?: string): Promise<IntegrationResourceModel> {
  const query = integrationId ? `?integrationId=${integrationId}` : "";
  const { data } = await apiReq("DELETE", `/v1/integrationResources/${id}${query}`);

  return IntegrationResourceModel.fromServerData(data);
}

export async function deleteResourcePermission(id: string, roleId: string, integrationId?: string): Promise<TFullIntegrationResourceModel> {
  const integrationIdParam = integrationId ? `&integrationId=${integrationId}` : "";
  const url = `/v1/integrationResources/${id}/deleteResourcePermission?roleId=${roleId}${integrationIdParam}`;
  const { data } = await apiReq("DELETE", url);

  return IntegrationResourceModel.fromServerData(data) as TFullIntegrationResourceModel;
}

export async function editIntegrationResource(
  integrationResource: Require<Partial<TIntegrationResourceCreateBody>, "id">,
): Promise<IntegrationResourceModel> {
  const { data } = await apiReq("PUT", `/v1/integrationResources/${integrationResource.id}`, integrationResource);

  return IntegrationResourceModel.fromServerData(data);
}

export async function getIntegrationResource(
  integrationResourceId: string,
  queryData: TIntegrationResourceQueryString = {},
): Promise<TFullIntegrationResourceModel> {
  const query = toQuerystring(queryData);
  const { data } = await apiReq("GET", `/v1/integrationResources/${integrationResourceId}?${query}`);
  return IntegrationResourceModel.fromServerData(data) as TFullIntegrationResourceModel;
}

export async function getIntegrationResources(
  options: TPaginationSortFilterOptions & {
    abortKey?: string;
    includeSubordinateIntegrations?: boolean;
    onlyFgaAuthorized?: boolean;
    search?: string;
  },
): Promise<IPaginationResponse<TFullIntegrationResourceModel>> {
  const searchParams = fromPaginationSortFilterOptions(options);
  if (options.search) {
    addSearchParam(searchParams, options.search);
  }
  if (options.onlyFgaAuthorized) {
    searchParams.append("onlyFgaAuthorized", "true");
  }
  if (options.includeSubordinateIntegrations) {
    searchParams.append("includeSubordinateIntegrations", "true");
  }
  const querystring = searchParams?.size ? `?${searchParams.toString()}` : "";
  return withPagination(
    (resource: Record<string, unknown>) =>
      IntegrationResourceModel.fromServerData(resource) as TFullIntegrationResourceModel,
    () =>
      apiReqAbortOld({
        key: options.abortKey ?? "integrationResources",
        method: "GET",
        path: `/v1/integrationResources${querystring}`,
      }),
  );
}

export async function getIntegrationResourceTags(search?: string): Promise<string[]> {
  const urlParams = new URLSearchParams();
  if (search) {
    urlParams.append("search", search);
  }

  const { data } = await apiReq("GET", `/v1/integrationResources/tags?${urlParams}`);
  if (isStringArray(data)) {
    return data;
  }
  devLog({ extra: { data }, level: "error", message: "Integration resource tags response is not an array" });
  return [];
}

export async function getIntegrationResourceTypes(
  search?: string,
  integrationIds?: string[],
  options?: { abortKey?: string; includeSubordinateIntegrations?: boolean; onlyFgaAuthorized?: boolean },
): Promise<string[]> {
  const urlParams = new URLSearchParams();
  if (search) {
    urlParams.append("search", search);
  }

  if (integrationIds?.length) {
    integrationIds.forEach((id) => urlParams.append("integrationId", id));
  }

  if (options?.onlyFgaAuthorized) {
    urlParams.append("onlyFgaAuthorized", "true");
  }
  if (options?.includeSubordinateIntegrations) {
    urlParams.append("includeSubordinateIntegrations", "true");
  }

  const { data } = await apiReqAbortOld({
    key: options?.abortKey ?? "integrationResourceTypes",
    method: "GET",
    path: `/v1/integrationResources/types?${urlParams}`,
  });
  if (isStringArray(data)) {
    return data;
  }
  devLog({ extra: { data }, level: "error", message: "Integration resource types response is not an array" });
  return [];
}

export async function searchIntegrationResources(
  options: TPaginationSortOptions & { abortKey?: string; filters?: ISectionedFilters; onlyFgaAuthorized?: boolean; search?: string },
): Promise<IPaginationResponse<TFullIntegrationResourceModel>> {
  const body: Record<string, unknown> = paginationSortFilterToBody(options, false);
  if (options.onlyFgaAuthorized) body.onlyFgaAuthorized = options.onlyFgaAuthorized;

  return withPagination(
    (resource: Record<string, unknown>) =>
      IntegrationResourceModel.fromServerData(resource) as TFullIntegrationResourceModel,
    () =>
      apiReqAbortOld({
        body,
        key: options.abortKey ?? "searchIntegrationResources",
        method: "POST",
        path: "/v1/integrationResources/search",
      }),
  );
}
